import { usePracticeStats } from '../../../../hooks/usePracticeStats';

// rounded-xl/border/shadow-sm (not the rounded-2xl "practice card" look used
// elsewhere) - this matches the design's Patients card treatment exactly, per
// the coordinator's screenshot transcription.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm';
const STAT_CELL_COUNT = 5;

function SkeletonCell() {
    return (
        <div className="flex-1 min-w-0 border-r border-gray-100 last:border-r-0 px-4 py-3.5">
            <div className="h-2.5 w-20 rounded bg-gray-200 animate-pulse" />
            <div className="h-6 w-10 rounded bg-gray-200 animate-pulse mt-2" />
        </div>
    );
}

interface StatCellProps {
    label: string;
    value: number;
    /**
     * The design deep-links each figure into a filtered patient list, but the
     * Patients tab has no status filter yet, so cells are plain this round -
     * no chevron, no hover affordance implying a click. Revisit once that
     * filter exists.
     */
    attention?: boolean;
}

// Pending action gets the amber dot when its count is > 0. No background
// tint - a tinted cell reads as selected next to its plain siblings.
function StatCell({ label, value, attention = false }: StatCellProps) {
    return (
        <div className="flex-1 min-w-0 border-r border-gray-100 last:border-r-0 px-4 py-3.5">
            <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-semibold text-gray-500 truncate">{label}</span>
                {attention && (
                    <span
                        className="w-1.5 h-1.5 rounded-full shrink-0"
                        style={{ background: '#FECA42' }}
                        title="Needs attention"
                    />
                )}
            </div>
            <div className="text-[28px] leading-none font-bold tabular-nums text-gray-900 mt-1.5">
                {value}
            </div>
        </div>
    );
}

interface OverviewStatsCardProps {
    practiceId: string;
}

export function OverviewStatsCard({ practiceId }: OverviewStatsCardProps) {
    const { stats, isLoading } = usePracticeStats(practiceId);

    return (
        <div className={CARD_CLASS}>
            <div className="px-5 py-3 border-b border-gray-100">
                <h3 className="text-[15px] font-bold text-gray-800">Patients</h3>
            </div>
            {isLoading ? (
                <div className="flex">
                    {Array.from({ length: STAT_CELL_COUNT }).map((_, i) => (
                        <SkeletonCell key={i} />
                    ))}
                </div>
            ) : !stats || stats.total === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No patients submitted yet.</p>
            ) : (
                <div className="flex">
                    <StatCell label="Total patients" value={stats.total} />
                    <StatCell label="In treatment" value={stats.inTreatment} />
                    <StatCell label="On hold" value={stats.onHold} />
                    <StatCell
                        label="Pending action"
                        value={stats.pendingAction}
                        attention={stats.pendingAction > 0}
                    />
                    <StatCell label="Completed" value={stats.completed} />
                </div>
            )}
        </div>
    );
}
